import React, { useMemo, useState } from 'react';
import { Box, Button, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { RotateCw } from 'lucide-react';
import { sbg } from '../../theme/sbgTokens';
import {
  EmptyState,
  KpiGrid,
  KpiTile,
  Meter,
  PersonCell,
  SearchField,
  SectionCard,
  SegmentedControl,
  SpecGrid,
  StatusPill,
  Tag,
  TableCard,
  TableEmptyRow,
  Toolbar,
} from '../ui';
import { PageError } from '../common/PageState';
import { MISSING, formatCount, formatDate, formatDateRange, formatNumber, formatTime } from '../../utils/format';
import { addDaysToYmd, getUpcomingWeekendStartEt, getWeekendStartForYmd } from '../../utils/easternTime';
import {
  TeamWeekendCheckpoint,
  TeamWeekendCheckpointStatus,
  TeamWeekendRoster,
  TeamWeekendRosterRep,
  useGetTeamWeekendAnalyticsQuery,
  useGetTeamWeekendRosterQuery,
} from '../../store/slices/teamAttendanceSlice';
import {
  DateStepper,
  SectionError,
  SectionLoading,
  SectionStalled,
  TableTitle,
  TeamSection,
  ToolbarNote,
  freshnessLabel,
  matchesRepSearch,
  noMatchTitle,
  nowrapSegmentsSx,
  repCellSx,
  sentenceCaseLabel,
  spansSeveralTeams,
  stickyFirstColumnSx,
  useReportMeta,
  useStalled,
} from './attendanceUi';
import { ScopeNote, describeQueryError, isAccessError, scopeMeta } from './TeamScopeNote';

const HISTORY_OPTIONS = [4, 8, 12];

/** Long rep lists (an admin sees every rep) scroll inside their card under a sticky header. */
const LONG_TABLE_MAX_HEIGHT = 'min(640px, 75vh)';

type RosterFilter = 'all' | 'signed_up' | 'no_shifts' | 'attention';
type HolidayDay = 'friday' | 'monday' | null;

const needsAttention = (rep: TeamWeekendRosterRep) => rep.suspension.isSuspended || rep.missedCheckins > 0;

const matchesRosterFilter = (rep: TeamWeekendRosterRep, filter: RosterFilter) => {
  switch (filter) {
    case 'signed_up':
      return rep.shiftCount > 0;
    case 'no_shifts':
      return rep.shiftCount === 0;
    case 'attention':
      return needsAttention(rep);
    default:
      return true;
  }
};

/** Problems first (missed check-ins, suspensions), then reps with shifts, then reps without. */
const rosterRank = (rep: TeamWeekendRosterRep) => (needsAttention(rep) ? 0 : rep.shiftCount > 0 ? 1 : 2);

/** The weekend's calendar days, including a holiday Friday or Monday: "Oct 10–12, 2026". */
const weekendSpan = (weekendStartDate: string, holidayDay: HolidayDay) => {
  const start = holidayDay === 'friday' ? addDaysToYmd(weekendStartDate, -1) : weekendStartDate;
  const end = addDaysToYmd(weekendStartDate, holidayDay === 'monday' ? 2 : 1);
  return formatDateRange(start, end);
};

const weekendRangeLabel = (roster: TeamWeekendRoster) =>
  weekendSpan(roster.weekendStartDate, roster.holiday.enabled ? roster.holiday.holidayDay : null);

/**
 * The history payload names a holiday day only inside its long label
 * ("Saturday, …, Sunday, …, and Monday, October 12, 2026"), so read it from there.
 */
const holidayDayFromLabel = (label: string): HolidayDay =>
  /^Friday\b/.test(label) ? 'friday' : /\bMonday\b/.test(label) ? 'monday' : null;

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "Friday, December 4, 2026" → "Dec 4, 2026"; a label in any other shape passes through. */
const shortDateFromLabel = (label: string | null) => {
  if (!label) return MISSING;
  const match = /([A-Z][a-z]+) (\d{1,2}), (\d{4})/.exec(label);
  const month = match ? MONTHS.indexOf(match[1]) : -1;
  if (!match || month < 0) return label;
  return formatDate(`${match[3]}-${String(month + 1).padStart(2, '0')}-${match[2].padStart(2, '0')}`);
};

/** One vocabulary for a check-in across both tabs. */
const CHECKPOINT_WORD: Record<TeamWeekendCheckpointStatus, string> = {
  scheduled: 'scheduled',
  awaiting_checkin: 'awaiting check-in',
  confirmed: 'confirmed',
  offsite_click: 'offsite check-in',
  window_closed: 'missed',
};

const checkpointText = (which: 'Start' | 'End', checkpoint: TeamWeekendCheckpoint) => {
  if (checkpoint.status === 'confirmed' && checkpoint.at) return `${which} confirmed ${formatTime(checkpoint.at)}`;
  return `${which} ${CHECKPOINT_WORD[checkpoint.status] ?? sentenceCaseLabel(checkpoint.label).toLowerCase()}`;
};

/** The one outcome per shift worth a pill: a missed or offsite check-in. Everything else reads as text. */
const shiftProblem = (shift: TeamWeekendRosterRep['shifts'][number]) => {
  const missedStart = shift.start.status === 'window_closed';
  const missedEnd = shift.end.status === 'window_closed';
  if (missedStart && missedEnd) return 'Missed start and end';
  if (missedStart) return 'Missed start';
  if (missedEnd) return 'Missed end';
  if (shift.start.status === 'offsite_click' || shift.end.status === 'offsite_click') return 'Offsite check-in';
  return null;
};

const rosterEmptyCopy = (filter: RosterFilter, search: string) => {
  if (search.trim()) return { title: noMatchTitle(search), body: 'Try a different name or team, or show every rep.' };
  switch (filter) {
    case 'attention':
      return { title: 'No one needs attention', body: 'No missed check-ins or suspensions this weekend.' };
    case 'signed_up':
      return { title: 'No one has signed up yet', body: 'Reps appear here once they pick a shift for this weekend.' };
    case 'no_shifts':
      return { title: 'Every rep has a shift', body: 'Everyone on this roster signed up for this weekend.' };
    default:
      return { title: 'No reps on this roster', body: 'Reps appear here once they are on your team.' };
  }
};

/** The status column: a pill only for a suspension, which is a true state; otherwise the shift count in words. */
const RosterStatusCell: React.FC<{ rep: TeamWeekendRosterRep }> = ({ rep }) => {
  if (rep.suspension.isSuspended) {
    return (
      <Box sx={{ display: 'grid', gap: 0.5, justifyItems: 'start' }}>
        <StatusPill tone="danger">Suspended</StatusPill>
        <Typography variant="body2" sx={{ color: sbg.text.secondary }}>
          {`Until ${shortDateFromLabel(rep.suspension.endsOnLabel)}`}
        </Typography>
        {rep.suspension.lastViolationReason ? (
          <Typography variant="body2" sx={{ color: sbg.text.secondary }}>
            {rep.suspension.lastViolationReason}
          </Typography>
        ) : null}
      </Box>
    );
  }
  return (
    <Typography variant="body1" component="span" sx={{ color: sbg.text.primary, fontVariantNumeric: 'tabular-nums' }}>
      {rep.shiftCount > 0 ? formatCount(rep.shiftCount, 'shift') : 'No shifts'}
    </Typography>
  );
};

const ShiftList: React.FC<{ rep: TeamWeekendRosterRep }> = ({ rep }) => {
  if (rep.shifts.length === 0) {
    return (
      <Typography variant="body1" component="span" sx={{ color: sbg.text.tertiary }}>
        {MISSING}
      </Typography>
    );
  }
  return (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
      {rep.shifts.map((shift) => {
        const problem = shiftProblem(shift);
        return (
          <Box component="li" key={shift.key} sx={{ display: 'grid', gap: 0.25 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1, rowGap: 0.5 }}>
              <Typography variant="body1" component="span" sx={{ color: sbg.text.primary }}>
                {shift.label}
              </Typography>
              {problem && <StatusPill tone="danger">{problem}</StatusPill>}
            </Box>
            <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
              {`${checkpointText('Start', shift.start)} · ${checkpointText('End', shift.end)}`}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

/** One shift's floor-wide fill: a meter against its cap, or a plain count when it has none. */
const ShiftFill: React.FC<{ name: string; availability: TeamWeekendRoster['shiftAvailability'][number] }> = ({ name, availability }) => {
  const { count, capacity, isFull, uncapped } = availability;
  if (uncapped || capacity === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignSelf: 'start' }}>
        <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
          {name}
        </Typography>
        <Typography variant="labelSm" component="span" sx={{ color: sbg.text.primary, fontVariantNumeric: 'tabular-nums' }}>
          {`${formatNumber(count)} signed up · No cap`}
        </Typography>
      </Box>
    );
  }
  return (
    <Meter
      label={name}
      valueLabel={`${formatNumber(count)} of ${formatNumber(capacity)}${isFull ? ' · Full' : ''}`}
      value={count}
      max={capacity}
      aria-label={`${name}: ${count} of ${capacity} spots taken`}
    />
  );
};

const TeamWeekendPanel: React.FC<{
  /** Receives the header meta (scope · weekend · freshness) for the page header. */
  onMetaChange?: (parts: string[]) => void;
}> = ({ onMetaChange }) => {
  // Undefined lets the server pick the active weekend, which stays on an
  // in-progress holiday-Monday weekend instead of jumping ahead.
  const [weekendStartDate, setWeekendStartDate] = useState<string | undefined>(undefined);
  const [historyWeekends, setHistoryWeekends] = useState(8);
  const [search, setSearch] = useState('');
  const [rosterFilter, setRosterFilter] = useState<RosterFilter>('all');

  // History covers completed weekends only: it ends the weekend before the
  // upcoming one.
  const historyEnd = useMemo(() => addDaysToYmd(getUpcomingWeekendStartEt(), -7), []);
  const historyStart = addDaysToYmd(historyEnd, -7 * (historyWeekends - 1));

  const roster = useGetTeamWeekendRosterQuery(weekendStartDate ? { weekendStartDate } : {});
  const analytics = useGetTeamWeekendAnalyticsQuery({ startWeekendDate: historyStart, endWeekendDate: historyEnd });

  // Each section loads, fails and refreshes on its own, so one slow source never hides the other.
  const rosterStalled = useStalled(Boolean(roster.isLoading || roster.isFetching), weekendStartDate ?? 'active');
  const historyStalled = useStalled(Boolean(analytics.isLoading || analytics.isFetching), `${historyStart}:${historyEnd}`);

  const sortedRoster = useMemo(
    () => [...(roster.data?.reps || [])].sort((left, right) => rosterRank(left) - rosterRank(right) || left.name.localeCompare(right.name)),
    [roster.data]
  );

  // The weekend the controls point at; the server's answer when it is for that weekend.
  const selectedWeekend = weekendStartDate ?? roster.data?.weekendStartDate;
  const rosterCurrent = Boolean(roster.data) && (!weekendStartDate || roster.data?.weekendStartDate === weekendStartDate);
  const weekendLabel =
    rosterCurrent && roster.data
      ? `Weekend of ${weekendRangeLabel(roster.data)}`
      : weekendStartDate
        ? `Weekend of ${weekendSpan(weekendStartDate, null)}`
        : 'This weekend';

  const scope = roster.data?.scope ?? analytics.data?.scope;
  const meta = [...scopeMeta(scope), weekendLabel, freshnessLabel([roster, analytics])].filter(
    (part): part is string => Boolean(part)
  );
  useReportMeta(onMetaChange, meta);

  // A refetch is ignored while a request is still pending, so the button only works when one can run.
  const bothPending = Boolean(roster.isFetching && analytics.isFetching);
  const refreshAll = () => {
    roster.refetch();
    analytics.refetch();
  };
  const searching = search.trim() !== '';
  const filtersActive = searching || rosterFilter !== 'all';
  const clearFilters = () => {
    setSearch('');
    setRosterFilter('all');
  };

  const toolbar = (
    <Toolbar
      sx={{ alignItems: 'flex-end', rowGap: 2 }}
      end={
        <Tooltip title={bothPending ? 'Already loading the latest data' : 'Refresh data'} describeChild>
          <span>
            <IconButton aria-label="Refresh data" onClick={refreshAll} disabled={bothPending}>
              <RotateCw />
            </IconButton>
          </span>
        </Tooltip>
      }
    >
      <DateStepper
        label="Weekend of"
        value={selectedWeekend || ''}
        disabled={!selectedWeekend}
        onChange={(value) => setWeekendStartDate(getWeekendStartForYmd(value))}
        onPrevious={() => selectedWeekend && setWeekendStartDate(addDaysToYmd(selectedWeekend, -7))}
        onNext={() => selectedWeekend && setWeekendStartDate(addDaysToYmd(selectedWeekend, 7))}
        previousLabel="Previous weekend"
        nextLabel="Next weekend"
      />
      {weekendStartDate !== undefined && (
        <Button variant="text" onClick={() => setWeekendStartDate(undefined)}>
          Go to this weekend
        </Button>
      )}
      <SearchField label="Search reps" placeholder="Search reps or teams" value={search} onChange={setSearch} width={260} />
    </Toolbar>
  );

  /* ---- The roster ------------------------------------------------------- */

  let rosterBody: React.ReactNode;
  if (rosterStalled) {
    rosterBody = <SectionStalled title="Couldn’t load the weekend roster" />;
  } else if (roster.error && !roster.isFetching) {
    rosterBody = (
      <SectionError title="Couldn’t load the weekend roster" body={describeQueryError(roster.error)} onRetry={() => roster.refetch()} />
    );
  } else if (!roster.data) {
    rosterBody = (
      <SectionLoading key={weekendStartDate ?? 'active'} label="Loading team weekend shifts…" summary="tiles" card columns={3} />
    );
  } else {
    const { summary } = roster.data;
    const missedCheckins = summary.missedStartCount + summary.missedEndCount;
    const optionByKey = new Map(roster.data.shiftOptions.map((option) => [option.key, option]));
    const showRosterTeam = spansSeveralTeams(roster.data.reps);
    const visibleRoster = sortedRoster.filter((rep) => matchesRepSearch(rep, search) && matchesRosterFilter(rep, rosterFilter));
    const attentionCount = roster.data.reps.filter(needsAttention).length;
    const rosterColumns = showRosterTeam ? 4 : 3;
    const emptyCopy = rosterEmptyCopy(rosterFilter, search);

    rosterBody = (
      <>
        <KpiGrid phoneColumns={2} sx={{ mb: 0 }}>
          <KpiTile
            label="Signed up"
            value={formatNumber(summary.signedUp)}
            caption={`of ${formatCount(summary.teamSize, 'rep')} · ${formatCount(summary.totalShiftSignups, 'shift')} picked`}
          />
          <KpiTile label="No shifts" value={formatNumber(summary.withoutShifts)} caption="Reps without a shift this weekend" />
          <KpiTile
            label="Missed check-ins"
            value={formatNumber(missedCheckins)}
            caption={`${formatNumber(summary.missedStartCount)} start · ${formatNumber(summary.missedEndCount)} end · ${formatCount(summary.missedRepCount, 'rep')}`}
            onClick={() => setRosterFilter('attention')}
            aria-label={`Missed check-ins: ${formatNumber(missedCheckins)}. Show reps who need attention`}
          />
          <KpiTile
            label="Suspended"
            value={formatNumber(summary.suspendedCount)}
            caption="Can’t sign up until it ends"
            info="A missed check-in suspends weekend sign-ups for a set period."
          />
        </KpiGrid>

        {roster.data.shiftAvailability.length > 0 ? (
          <SectionCard dense component="div">
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" component="h3" sx={{ color: sbg.text.primary }}>
                  Floor-wide shift fill
                </Typography>
                <Typography variant="body2" sx={{ color: sbg.text.secondary, mt: 0.25 }}>
                  Spots taken across all sales reps, not only your team.
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                  columnGap: 3,
                  rowGap: 2,
                }}
              >
                {roster.data.shiftAvailability.map((availability) => (
                  <ShiftFill
                    key={availability.key}
                    name={optionByKey.get(availability.key)?.shortLabel || availability.key}
                    availability={availability}
                  />
                ))}
              </Box>
            </Box>
          </SectionCard>
        ) : null}

        <TableCard
          aria-label="Weekend roster for your team"
          maxHeight={LONG_TABLE_MAX_HEIGHT}
          toolbar={
            <Toolbar end={<ToolbarNote>{formatCount(visibleRoster.length, 'rep')}</ToolbarNote>}>
              <Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                <SegmentedControl<RosterFilter>
                  size="small"
                  aria-label="Show reps"
                  value={rosterFilter}
                  onChange={setRosterFilter}
                  sx={nowrapSegmentsSx}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'signed_up', label: 'Signed up' },
                    { value: 'no_shifts', label: 'No shifts' },
                    { value: 'attention', label: `Needs attention (${formatNumber(attentionCount)})` },
                  ]}
                />
              </Box>
            </Toolbar>
          }
        >
          <Table stickyHeader aria-label="Team weekend roster" sx={stickyFirstColumnSx}>
            <TableHead>
              <TableRow>
                <TableCell>Rep</TableCell>
                {showRosterTeam && <TableCell>Team</TableCell>}
                <TableCell>Status</TableCell>
                <TableCell>Shifts and check-ins</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRoster.length === 0 ? (
                <TableEmptyRow
                  colSpan={rosterColumns}
                  title={emptyCopy.title}
                  body={emptyCopy.body}
                  action={
                    filtersActive ? (
                      <Button variant="outlined" size="small" onClick={clearFilters}>
                        Clear filters
                      </Button>
                    ) : undefined
                  }
                />
              ) : (
                visibleRoster.map((rep) => (
                  <TableRow key={rep.employeeId} sx={{ '& > td': { verticalAlign: 'top' } }}>
                    <TableCell sx={repCellSx}>
                      <PersonCell name={rep.name} size={24} />
                    </TableCell>
                    {showRosterTeam && <TableCell sx={{ color: sbg.text.secondary, whiteSpace: 'nowrap' }}>{rep.teamName || MISSING}</TableCell>}
                    <TableCell sx={{ minWidth: 160 }}>
                      <RosterStatusCell rep={rep} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 300 }}>
                      <ShiftList rep={rep} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableCard>
      </>
    );
  }

  /* ---- History ---------------------------------------------------------- */

  let historyBody: React.ReactNode;
  if (historyStalled) {
    historyBody = <SectionStalled title="Couldn’t load weekend history" />;
  } else if (analytics.error && !analytics.isFetching) {
    historyBody = (
      <SectionError title="Couldn’t load weekend history" body={describeQueryError(analytics.error)} onRetry={() => analytics.refetch()} />
    );
  } else if (!analytics.data) {
    historyBody = (
      <SectionLoading key={`${historyStart}:${historyEnd}`} label="Loading weekend history…" summary="spec" columns={8} tables={2} />
    );
  } else {
    const history = analytics.data;
    const showHistoryTeam = spansSeveralTeams(history.reps);
    const visibleHistoryReps = history.reps.filter((rep) => matchesRepSearch(rep, search));
    const historyColumns = showHistoryTeam ? 9 : 8;

    historyBody = (
      <>
        <SpecGrid
          items={[
            {
              label: 'Shift sign-ups',
              value: formatNumber(history.totals.totalShiftSignups),
              caption: `${formatCount(history.totals.signedUpRepCount, 'rep')} signed up`,
            },
            {
              label: 'Reps with no shifts',
              value: formatNumber(history.totals.zeroSignupRepCount),
              caption: 'Across the whole range',
            },
            {
              label: 'Missed starts',
              value: formatNumber(history.totals.missedStartCheckins),
              caption: formatCount(history.totals.offsiteStartClicks, 'offsite check-in'),
            },
            {
              label: 'Missed ends',
              value: formatNumber(history.totals.missedEndCheckins),
              caption: formatCount(history.totals.offsiteEndClicks, 'offsite check-in'),
            },
          ]}
        />

        <TableCard
          aria-label="Weekend history by rep"
          maxHeight={LONG_TABLE_MAX_HEIGHT}
          toolbar={
            <Toolbar>
              <TableTitle caption={formatCount(visibleHistoryReps.length, 'rep')}>By rep</TableTitle>
            </Toolbar>
          }
        >
          <Table stickyHeader aria-label="Weekend shifts by rep" sx={stickyFirstColumnSx}>
            <TableHead>
              <TableRow>
                <TableCell>Rep</TableCell>
                {showHistoryTeam && <TableCell>Team</TableCell>}
                <TableCell align="right">Shifts</TableCell>
                <TableCell align="right">Weekends worked</TableCell>
                <TableCell align="right">Weekends off</TableCell>
                <TableCell align="right">Missed starts</TableCell>
                <TableCell align="right">Missed ends</TableCell>
                <TableCell align="right">Offsite check-ins</TableCell>
                <TableCell>Suspension</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleHistoryReps.length === 0 ? (
                <TableEmptyRow
                  colSpan={historyColumns}
                  title={searching ? noMatchTitle(search) : 'No rep activity in this range'}
                  body={searching ? 'Try a different name or team.' : 'Reps appear here once they are on your team.'}
                  action={
                    searching ? (
                      <Button variant="outlined" size="small" onClick={() => setSearch('')}>
                        Clear search
                      </Button>
                    ) : undefined
                  }
                />
              ) : (
                visibleHistoryReps.map((rep) => (
                  <TableRow key={rep.employeeId}>
                    <TableCell sx={repCellSx}>
                      <PersonCell name={rep.name} size={24} />
                    </TableCell>
                    {showHistoryTeam && <TableCell sx={{ color: sbg.text.secondary, whiteSpace: 'nowrap' }}>{rep.teamName || MISSING}</TableCell>}
                    <TableCell align="right">{formatNumber(rep.totalShiftSignups)}</TableCell>
                    <TableCell align="right">{formatNumber(rep.weekendsSignedUp)}</TableCell>
                    <TableCell align="right">{formatNumber(rep.weekendsWithoutShifts)}</TableCell>
                    <TableCell align="right">{formatNumber(rep.missedStartCount)}</TableCell>
                    <TableCell align="right">{formatNumber(rep.missedEndCount)}</TableCell>
                    <TableCell align="right">{formatNumber(rep.offsiteStartCount + rep.offsiteEndCount)}</TableCell>
                    <TableCell>
                      {rep.isSuspended ? (
                        <StatusPill tone="danger">Suspended</StatusPill>
                      ) : (
                        <Box component="span" sx={{ color: sbg.text.tertiary }}>
                          {MISSING}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableCard>

        <TableCard
          aria-label="Weekend history by weekend"
          toolbar={
            <Toolbar>
              <TableTitle caption={formatCount(history.weekends.length, 'weekend')}>By weekend</TableTitle>
            </Toolbar>
          }
        >
          <Table aria-label="Weekend trend">
            <TableHead>
              <TableRow>
                <TableCell>Weekend</TableCell>
                <TableCell align="right">Signed up</TableCell>
                <TableCell align="right">No shifts</TableCell>
                <TableCell align="right">Shifts</TableCell>
                <TableCell align="right">Missed starts</TableCell>
                <TableCell align="right">Missed ends</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.weekends.length === 0 ? (
                <TableEmptyRow
                  colSpan={6}
                  title="No completed weekends in this range"
                  body="Weekends appear here once they finish."
                />
              ) : (
                history.weekends.map((weekend) => {
                  const holidayDay = holidayDayFromLabel(weekend.weekendLabel);
                  return (
                    <TableRow key={weekend.weekendStartDate}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                          {weekendSpan(weekend.weekendStartDate, holidayDay)}
                          {holidayDay && <Tag>{holidayDay === 'friday' ? 'Holiday Fri' : 'Holiday Mon'}</Tag>}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(weekend.signedUp)} of {formatNumber(weekend.teamSize)}
                      </TableCell>
                      <TableCell align="right">{formatNumber(weekend.withoutShifts)}</TableCell>
                      <TableCell align="right">{formatNumber(weekend.totalShiftSignups)}</TableCell>
                      <TableCell align="right">{formatNumber(weekend.missedStartCount)}</TableCell>
                      <TableCell align="right">{formatNumber(weekend.missedEndCount)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableCard>
      </>
    );
  }

  /* ---- The panel -------------------------------------------------------- */

  const accessError = [roster, analytics].find((query) => isAccessError(query.error) && !query.isFetching)?.error;
  const teamEmpty = scope?.teamSize === 0 && (analytics.data?.reps.length ?? 0) === 0;
  const holiday = rosterCurrent && roster.data?.holiday.enabled ? roster.data.holiday.holidayLabel : null;

  let content: React.ReactNode;
  if (accessError) {
    content = <PageError title="Couldn’t load team weekend shifts" description={describeQueryError(accessError)} onRetry={refreshAll} />;
  } else if (teamEmpty) {
    content = (
      <>
        <ScopeNote scope={scope} />
        <SectionCard>
          <EmptyState
            title="No reps on your team yet"
            body="Reps you manage appear here once an admin links them to you in their employee record."
          />
        </SectionCard>
      </>
    );
  } else {
    content = (
      <>
        <ScopeNote scope={scope} />

        <TeamSection
          id="team-weekend-roster"
          title={weekendLabel}
          badge={holiday ? <Tag>{holiday}</Tag> : null}
          description="Each shift needs an office check-in at its start and end. A missed check-in can lead to a suspension."
          refreshing={Boolean(roster.data) && roster.isFetching && !rosterStalled}
          refreshLabel="Refreshing the weekend roster"
        >
          {rosterBody}
        </TeamSection>

        <TeamSection
          id="team-weekend-history"
          title={`Last ${historyWeekends} weekends`}
          description={`${formatDateRange(historyStart, addDaysToYmd(historyEnd, 1))}. Completed weekends only.`}
          refreshing={Boolean(analytics.data) && analytics.isFetching && !historyStalled}
          refreshLabel="Refreshing weekend history"
          action={
            <SegmentedControl
              size="small"
              aria-label="History window"
              value={String(historyWeekends)}
              onChange={(value) => setHistoryWeekends(Number(value))}
              sx={nowrapSegmentsSx}
              options={HISTORY_OPTIONS.map((option) => ({ value: String(option), label: `${option} weekends` }))}
            />
          }
        >
          {historyBody}
        </TeamSection>
      </>
    );
  }

  return (
    <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
      {toolbar}
      <Box sx={{ display: 'grid', gap: 5, minWidth: 0 }}>{content}</Box>
    </Box>
  );
};

export default TeamWeekendPanel;
